import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useFilters } from '../contexts/FilterContext';
import useReport from '../lib/useReport';
import { money, int, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty } from '../components/ui';
import PeriodPicker from '../components/PeriodPicker';
import { periodContaining, shiftPeriod } from '../lib/payPeriods';
import { hmm, decimalHours, splitOvertime, payCents, wageSuspicious } from '../lib/timesheetMath';
import { downloadText } from '../lib/exports/download';
import {
  flattenShifts, employeeRows, buildTimesheetCsv, buildTimesheetTotalsCsv,
  buildTimeactIif, buildQboTimesheetCsv, buildAdpCsv, payrollName,
} from '../lib/exports/timesheets';

// Timesheets — the POS employee timesheet (clock-in/out per shift, hours per
// employee) for a pay period, exportable for payroll: generic CSV, QuickBooks
// Desktop IIF (TIMEACT), QuickBooks Online timesheet CSV, or ADP pay data.
// The pay period, payroll names and ADP ids come from the store's accounting
// profile; pay earned shows only when the profile asks for it.

const FORMATS = [
  { id: 'shifts', label: 'Shifts CSV (one row per shift)' },
  { id: 'totals', label: 'Totals CSV (one row per employee)' },
  { id: 'timeact', label: 'QuickBooks Desktop (IIF TIMEACT)' },
  { id: 'qbo', label: 'QuickBooks Online timesheet (CSV)' },
  { id: 'adp', label: 'ADP pay data (CSV)' },
];
const formatFor = (software) => (software === 'qbd' ? 'timeact' : software === 'qbo' ? 'qbo' : 'totals');

function EmployeeBlock({ e, profile, open, onToggle }) {
  const ts = profile.timesheets || {};
  const { reg, ot } = splitOvertime(e.shifts, ts.overtimeWeeklyHours);
  const flagged = e.shifts.filter(s => s.flag && !s.open).length; // open shifts have their own KPI
  const wageOk = e.hourlyWage !== null && e.hourlyWage !== undefined && !wageSuspicious(e.hourlyWage);
  const mapped = !!ts.qbNames?.[e.employeeId];
  const unnamed = !mapped && !e.firstName && !e.lastName;
  return (
    <>
      <tr className="ts-emp" onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td>
          <strong>{unnamed ? `Employee #${e.employeeId}` : payrollName(e, ts.qbNames)}</strong>
          {!unnamed && <span className="hint"> #{e.employeeId}</span>}{e.role && <span className="hint"> · {e.role}</span>}
          {unnamed && (
            <span className="pill bad" style={{ marginLeft: 6 }}
              title={e.nameCandidates?.length ? `No employee record from this store's terminals; other stores use this number for: ${e.nameCandidates.join(', ')}. Set the name in Accounting Profiles.` : 'No employee record found — set the name in Accounting Profiles.'}>
              name?
            </span>
          )}
          {e.active === false && <span className="pill" style={{ marginLeft: 6 }}>inactive</span>}
          {flagged > 0 && <span className="pill warn" style={{ marginLeft: 6 }}>{flagged} to check</span>}
        </td>
        <td className="num">{int(e.shifts.length)}</td>
        <td className="num"><strong>{hmm(e.minutes)}</strong></td>
        <td className="num">{decimalHours(e.minutes).toFixed(2)}</td>
        {ts.overtimeWeeklyHours && <td className="num">{decimalHours(reg).toFixed(2)} / <span className={ot ? 'pill warn' : ''}>{decimalHours(ot).toFixed(2)}</span></td>}
        {ts.showPay && (
          <td className="num">
            {wageOk ? money(payCents(e.minutes, e.hourlyWage)) : <span className="pill bad" title={e.hourlyWage === null ? 'no wage on file' : 'wage looks wrong at the POS'}>check wage</span>}
          </td>
        )}
        <td className="hint">{open ? '▾' : '▸'}</td>
      </tr>
      {open && e.shifts.map(s => (
        <tr key={s.id} className={`ts-shift ${s.flag ? 'flagged' : ''}`}>
          <td style={{ paddingLeft: 28 }}>{niceDate(s.date)} <span className="hint">{s.start.slice(11, 16)} → {s.end ? s.end.slice(11, 16) : '—'}</span>{s.flag && <span className="pill warn" style={{ marginLeft: 6 }}>{s.flag}</span>}</td>
          <td />
          <td className="num">{hmm(s.minutes)}</td>
          <td className="num">{decimalHours(s.minutes).toFixed(2)}</td>
          {ts.overtimeWeeklyHours && <td />}
          {ts.showPay && <td className="num">{wageOk ? money(payCents(s.minutes, e.hourlyWage)) : ''}</td>}
          <td />
        </tr>
      ))}
    </>
  );
}

export default function TimesheetsPage() {
  const { anchor, selectedLocations, locations, me } = useFilters();
  const [profiles, setProfiles] = useState(null);
  useEffect(() => {
    axios.get('/api/admin/accounting-profiles')
      .then(r => setProfiles(r.data))
      .catch(() => setProfiles({ profiles: [], default: null }));
  }, []);

  // Stores in view and the profile that drives the picker/exports: the first
  // selected store's (they usually share one), else the built-in default.
  const storeIds = selectedLocations.length ? selectedLocations.map(Number) : locations.map(l => Number(l.ID));
  const profileFor = (id) => (profiles?.profiles || []).find(p => p.locations.includes(Number(id)))
    || { ...(profiles?.default || {}), isDefault: true, timesheets: profiles?.default?.timesheets || {}, payPeriod: profiles?.default?.payPeriod || { type: 'biweekly', anchorDate: '2026-01-05' }, classes: {} };
  const profile = profileFor(storeIds[0]);
  const mixed = new Set(storeIds.map(id => profileFor(id).id)).size > 1;

  const [range, setRange] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sd-ts-period') || 'null');
      if (saved?.start && saved?.end) return saved;
    } catch { /* ignore */ }
    return null;
  });
  // Payroll runs after a period ends: default to the last period that has
  // fully elapsed as of the data's last complete day.
  useEffect(() => {
    if (!range && profiles) {
      let p = periodContaining(anchor, profile.payPeriod);
      if (p.end > anchor) p = shiftPeriod(p, -1);
      setRange({ start: p.start, end: p.end });
    }
  }, [profiles, anchor]);
  useEffect(() => { if (range) localStorage.setItem('sd-ts-period', JSON.stringify(range)); }, [range]);

  const params = useMemo(() => range && ({
    startDate: range.start, endDate: range.end,
    ...(selectedLocations.length > 0 ? { locations: selectedLocations.join(',') } : {}),
  }), [range, selectedLocations]);
  const { data, loading, error } = useReport('accounting/timesheets', params || {}, { enabled: !!params });

  const [format, setFormat] = useState(null);
  useEffect(() => { if (format === null && profiles) setFormat(formatFor(profile.software)); }, [profiles]);
  const [openKey, setOpenKey] = useState(null);

  const totals = useMemo(() => {
    const stores = data?.stores || [];
    return {
      minutes: stores.reduce((a, s) => a + s.minutes, 0),
      employees: stores.reduce((a, s) => a + s.employees.length, 0),
      shifts: stores.reduce((a, s) => a + s.employees.reduce((b, e) => b + e.shifts.length, 0), 0),
      open: stores.reduce((a, s) => a + s.openShifts, 0),
      flagged: stores.reduce((a, s) => a + s.flaggedShifts, 0),
    };
  }, [data]);

  const ts = profile.timesheets || {};
  const fmt = FORMATS.find(f => f.id === format) || FORMATS[1];
  const exportInfo = useMemo(() => {
    if (!data) return null;
    if (fmt.id === 'adp') {
      const r = buildAdpCsv(employeeRows(data), { coCode: ts.adp?.coCode, batchId: ts.adp?.batchId, fileNumbers: ts.adp?.fileNumbers, overtimeWeeklyHours: ts.overtimeWeeklyHours });
      return { ...r, warn: [!ts.adp?.coCode && 'no ADP company code in the profile', r.missing.length && `${r.missing.length} employee${r.missing.length > 1 ? 's' : ''} without an ADP file # (skipped): ${r.missing.map(e => `${e.firstName} ${e.lastName}`.trim()).join(', ')}`].filter(Boolean) };
    }
    if (fmt.id === 'timeact') { const r = buildTimeactIif(flattenShifts(data), { qbNames: ts.qbNames }); return { ...r, warn: r.skipped ? [`${r.skipped} open or zero-length shift${r.skipped > 1 ? 's' : ''} skipped`] : [] }; }
    if (fmt.id === 'qbo') { const r = buildQboTimesheetCsv(flattenShifts(data), { qbNames: ts.qbNames, classes: profile.classes }); return { ...r, warn: r.skipped ? [`${r.skipped} open or zero-length shift${r.skipped > 1 ? 's' : ''} skipped`] : [] }; }
    if (fmt.id === 'shifts') return { text: buildTimesheetCsv(flattenShifts(data)), count: totals.shifts, warn: [] };
    return { text: buildTimesheetTotalsCsv(employeeRows(data), { overtimeWeeklyHours: ts.overtimeWeeklyHours, showPay: ts.showPay }), count: totals.employees, warn: [] };
  }, [data, fmt.id, profile, totals]);

  const doExport = () => {
    const ext = fmt.id === 'timeact' ? 'iif' : 'csv';
    downloadText(`timesheets-${range.start}_${range.end}-${fmt.id}.${ext}`, exportInfo.text, ext === 'iif' ? 'text/plain;charset=utf-8' : 'text/csv;charset=utf-8');
  };

  if (!profiles || !range) return <Spinner label="Loading pay period…" />;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card tour="ts-period">
        <div className="chip-row" style={{ alignItems: 'center', gap: 14 }}>
          <PeriodPicker cfg={profile.payPeriod} value={range} onChange={setRange} maxDate={anchor} />
          <span className="hint">
            {profile.payPeriod.type === 'biweekly' ? 'two-week' : profile.payPeriod.type} pay periods from profile “{profile.name}”{profile.isDefault ? ' (built-in default)' : ''}
            {mixed && ' · stores in view use different profiles — the first store’s period applies'}
          </span>
        </div>
      </Card>

      {error && <ErrorBox message={error} />}
      {loading && !data && <Spinner label="Loading shifts…" />}
      {data && data.stores.length === 0 && <Empty>No shifts clocked in this period for the selected stores.</Empty>}

      {data && data.stores.length > 0 && (
        <>
          <div className="grid kpi-grid" data-tour="ts-kpis">
            <KpiCard label="Hours" value={hmm(totals.minutes)} sub={`${decimalHours(totals.minutes).toFixed(2)} decimal`} />
            <KpiCard label="Employees" value={int(totals.employees)} sub={`${int(totals.shifts)} shifts`} />
            <KpiCard label="Still clocked in" value={int(totals.open)} sub="open shifts count 0 h" />
            <KpiCard label="To check" value={int(totals.flagged)} sub="4 AM auto clock-outs, >16 h, <1 min" />
          </div>

          {data.stores.map(s => {
            const p = profileFor(s.locationId);
            return (
              <Card key={s.locationId} title={s.name} sub={`${s.employees.length} employees · ${hmm(s.minutes)} · ${data.basis}`} tour="ts-table">
                <div className="table-wrap">
                  <table className="data ts-table">
                    <thead>
                      <tr>
                        <th className="no-sort">Employee</th>
                        <th className="no-sort num">Shifts</th>
                        <th className="no-sort num">Hours</th>
                        <th className="no-sort num">Decimal</th>
                        {p.timesheets?.overtimeWeeklyHours && <th className="no-sort num">Reg / OT</th>}
                        {p.timesheets?.showPay && <th className="no-sort num">Pay</th>}
                        <th className="no-sort" style={{ width: 24 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {s.employees.map(e => {
                        const key = `${s.locationId}:${e.employeeId}`;
                        return <EmployeeBlock key={key} e={e} profile={p} open={openKey === key} onToggle={() => setOpenKey(k => (k === key ? null : key))} />;
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Store total</strong></td>
                        <td className="num">{int(s.employees.reduce((a, e) => a + e.shifts.length, 0))}</td>
                        <td className="num"><strong>{hmm(s.minutes)}</strong></td>
                        <td className="num">{decimalHours(s.minutes).toFixed(2)}</td>
                        {p.timesheets?.overtimeWeeklyHours && <td />}
                        {p.timesheets?.showPay && <td />}
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            );
          })}

          <Card title="Export for payroll" sub="every store in view for this period" tour="ts-export">
            <div className="chip-row" style={{ alignItems: 'center' }}>
              <select className="field" value={fmt.id} onChange={e => setFormat(e.target.value)}>
                {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <button className="btn-primary inline" disabled={!exportInfo || exportInfo.count === 0} onClick={doExport}>
                ⬇ Download {exportInfo?.count ?? 0} row{exportInfo?.count === 1 ? '' : 's'}
              </button>
            </div>
            {exportInfo?.warn?.length > 0 && (
              <div className="notice" style={{ marginTop: 8 }}>{exportInfo.warn.join(' · ')}</div>
            )}
            <div className="hint" style={{ marginTop: 8 }}>
              {fmt.id === 'timeact' && 'Import in QuickBooks Desktop via File › Utilities › Import › Timer Activities. Employee names must match QuickBooks exactly — set them per employee in the Accounting Profile.'}
              {fmt.id === 'qbo' && 'Import in QuickBooks Online (Advanced) via Settings › Import data › Timesheets.'}
              {fmt.id === 'adp' && 'ADP pay data import: company code, batch id and each employee’s File # come from the Accounting Profile.'}
              {fmt.id === 'shifts' && 'One row per clock-in with flags — the raw data for any payroll system or a spreadsheet.'}
              {fmt.id === 'totals' && 'One row per employee with hours, regular/overtime split and (if enabled) pay earned.'}
              {me?.role !== 'bookkeeper' && ' Payroll names, ADP ids and the pay period are set in Administration › Accounting Profiles.'}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
